import { useState, useCallback, useEffect, useRef } from 'react';
import { apiFetch } from '../../../config/api';
import { debugError, debugWarn } from '../../../utils/debugLog';

/**
 * Custom hook to handle Materialized View refresh with queue support
 *
 * This hook provides functionality to refresh the template analytics
 * materialized view using the queue system. It handles:
 * - Queue status (queued, processing, cooldown)
 * - Status polling while refresh is in progress
 * - Cooldown protection messages
 *
 * @returns {Object} Hook interface with refresh function and status
 */
export const useMvRefresh = () => {
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState(null);
  const [refreshStatus, setRefreshStatus] = useState(null); // Current queue status
  const [refreshId, setRefreshId] = useState(null); // Current refresh ID
  const pollingIntervalRef = useRef(null);

  /**
   * Get refresh status
   * @returns {Promise<Object>} Refresh status information
   */
  const getRefreshStatus = useCallback(async () => {
    try {
      const response = await apiFetch(
        '/api/v1/templates/analytics/refresh/status'
      );
      return response;
    } catch (err) {
      debugError('Error getting refresh status:', err);
      return null;
    }
  }, []);

  /**
   * Poll refresh status while refresh is in progress
   * @param {string} refreshId - Refresh ID to poll
   */
  const pollRefreshStatus = useCallback(
    async refreshId => {
      try {
        const status = await getRefreshStatus();

        if (status) {
          setRefreshStatus(status);

          // Stop polling if refresh is completed or failed
          if (
            status.status === 'completed' ||
            status.status === 'failed' ||
            status.status === 'idle'
          ) {
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
            setRefreshing(false);

            if (status.status === 'completed') {
              return {
                success: true,
                message:
                  status.message || 'Materialized view refreshed successfully',
                duration_seconds: status.duration_seconds || 0,
              };
            } else if (status.status === 'failed') {
              return {
                success: false,
                message:
                  status.error_message || status.message || 'Refresh failed',
              };
            }
          }
        }
      } catch (err) {
        debugError('Error polling refresh status:', err);
      }
      return null;
    },
    [getRefreshStatus]
  );

  /**
   * Refresh the materialized view (queue-based)
   * @returns {Promise<Object>} Refresh result with success status
   */
  const refreshMv = useCallback(async () => {
    setRefreshing(true);
    setRefreshError(null);
    setRefreshStatus(null);
    setRefreshId(null);

    // Clear any existing polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    try {
      // Note: API requires trailing slash for POST endpoints
      const response = await apiFetch('/api/v1/templates/analytics/refresh/', {
        method: 'POST',
      });

      if (!response) {
        throw new Error('No response from refresh endpoint');
      }

      const status = response.status || 'unknown';
      const message = response.message || 'Refresh request processed';

      // Handle different queue statuses
      if (status === 'cooldown') {
        // In cooldown - show message and return
        const cooldownMessage =
          response.message ||
          `Please wait ${response.cooldown_seconds || 3} seconds before refreshing again`;
        setRefreshError(cooldownMessage);
        setRefreshing(false);
        return {
          success: false,
          message: cooldownMessage,
          cooldown: true,
        };
      } else if (status === 'processing') {
        // Already processing - poll for completion
        setRefreshId(response.refresh_id);
        setRefreshStatus(response);
        // Start polling
        pollingIntervalRef.current = setInterval(() => {
          pollRefreshStatus(response.refresh_id);
        }, 2000); // Poll every 2 seconds
        return {
          success: true,
          message: 'Refresh is already in progress',
          status: 'processing',
          refresh_id: response.refresh_id,
        };
      } else if (status === 'pending') {
        // Queued - poll for status updates
        setRefreshId(response.refresh_id);
        setRefreshStatus(response);
        // Start polling
        pollingIntervalRef.current = setInterval(() => {
          pollRefreshStatus(response.refresh_id);
        }, 2000); // Poll every 2 seconds
        return {
          success: true,
          message: 'Refresh request queued',
          status: 'pending',
          refresh_id: response.refresh_id,
        };
      } else if (status === 'queued') {
        // Successfully queued - poll for status updates
        setRefreshId(response.refresh_id);
        setRefreshStatus(response);
        // Start polling
        pollingIntervalRef.current = setInterval(() => {
          pollRefreshStatus(response.refresh_id);
        }, 2000); // Poll every 2 seconds
        return {
          success: true,
          message: 'Refresh request queued successfully',
          status: 'queued',
          refresh_id: response.refresh_id,
        };
      } else {
        // Unknown status
        debugWarn('Unknown refresh status:', status);
        setRefreshing(false);
        return {
          success: false,
          message: message || 'Unknown refresh status',
        };
      }
    } catch (err) {
      const errorMessage =
        err.message || 'An error occurred while refreshing materialized view';
      setRefreshError(errorMessage);
      setRefreshing(false);
      debugError('Error refreshing materialized view:', err);
      return {
        success: false,
        message: errorMessage,
      };
    }
  }, [pollRefreshStatus]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  return {
    refreshing,
    refreshError,
    refreshStatus, // Current queue status
    refreshId, // Current refresh ID
    refreshMv,
    getRefreshStatus,
  };
};

export default useMvRefresh;
